// Persona row/facet renderers shared by the Personas table columns and the
// FilterBar facets, so a chip can never name a value the column does not
// render. Presentational only — no queries, no mutations.
import type { components } from "../../api/schema";
import { Badge, Copyable, Stack, Text } from "../../ui";
import {
  personaLlmProvider,
  personaPhoneEndpoint,
  personaVoiceSelection,
  providerLabel,
  voiceLabel,
} from "./personaSpec";

type PersonaProviderPolicy =
  components["schemas"]["PersonaProviderPolicyResponse"];
type PersonaResponse = components["schemas"]["PersonaResponse"];

// The number-selection facet/column value.
export function personaNumberLabel(persona: PersonaResponse): string {
  return personaPhoneEndpoint(persona).mode === "pinned"
    ? "Pinned from pool"
    : "Auto";
}

// The pinned value copies as the E.164 address once the org's inventory
// resolves the ref — that is the identifier a human recognizes. A ref the
// pool no longer carries (or one read while the inventory is still loading)
// falls back to the raw id, so a saved value is never hidden.
export function personaPhoneEndpointLabel(
  persona: PersonaResponse,
  addressesById: ReadonlyMap<string, string>,
) {
  const endpoint = personaPhoneEndpoint(persona);
  const poolNumberRef = endpoint.pool_number_ref;
  if (endpoint.mode === "pinned" && poolNumberRef) {
    const address = addressesById.get(poolNumberRef);
    return (
      <Stack gap={1}>
        <Badge tone="success">Pinned from pool</Badge>
        {address === undefined ? (
          <Copyable label="Pool number ref" value={poolNumberRef} />
        ) : (
          <Copyable label="Phone number" value={address} />
        )}
      </Stack>
    );
  }
  return <Badge tone="neutral">Auto</Badge>;
}

// Provider labels come from the /v1/me projection (#1441): the policy's
// display_name when the provider is enabled here, else the saved token
// verbatim — the SPA carries no provider labels of its own.
export function personaVoiceProviderLabel(
  persona: PersonaResponse,
  policy: PersonaProviderPolicy,
): string {
  const selection = personaVoiceSelection(persona);
  if (selection === null) {
    return "No voice";
  }
  return providerLabel(policy.voice_providers, selection.provider);
}

export function personaVoiceLabel(
  persona: PersonaResponse,
  policy: PersonaProviderPolicy,
) {
  const selection = personaVoiceSelection(persona);
  if (selection === null) {
    return <Text variant="muted">No voice</Text>;
  }
  // The catalog display label, never the provider-native ref: a ref is an id.
  // A saved voice this deployment no longer offers is named as unknown rather
  // than relabeled or hidden.
  const label = voiceLabel(
    policy.voice_providers,
    selection.provider,
    selection.voiceRef,
  );
  return (
    <Stack gap={1}>
      <Text>{providerLabel(policy.voice_providers, selection.provider)}</Text>
      <Text as="span" variant="muted">
        {selection.voiceRef.length === 0
          ? "Voice reference required"
          : (label ?? "Unknown voice")}
      </Text>
    </Stack>
  );
}

export function personaLlmLabel(
  persona: PersonaResponse,
  policy: PersonaProviderPolicy,
): string {
  const provider = personaLlmProvider(persona);
  if (provider.length === 0) {
    return "Not set";
  }
  return providerLabel(policy.llm_providers, provider);
}
